import React, { JSX } from 'react';
import JsonView from '@uiw/react-json-view';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { VectorVisualizer } from './VectorVisualizer';
import { MessageProps } from './Message';
import {
  ChatMessage,
  ChatMessageError,
  ChatMessageMetaData,
  ChatMessageStatus,
  ChromaDBGetResponse,
} from 'types/types';

interface MessageMetaProps {
  metadata: ChatMessageMetaData;
  messageProps: MessageProps;
}

export const MessageMeta = (props: MessageMetaProps): JSX.Element => {
  const { ragResults = [], tools = null } = props.metadata || {};
  const {
    evalCount = 0,
    evalDuration = 0,
    promptEvalCount = 0,
    promptEvalDuration = 0,
  } = props.metadata.usage || {};
  const message: ChatMessage | ChatMessageError | ChatMessageStatus = props.messageProps.message;

  return (
    <>
      {promptEvalDuration !== 0 && evalDuration !== 0 && (
        <PromptStats
          evalCount={evalCount}
          evalDuration={evalDuration}
          promptEvalCount={promptEvalCount}
          promptEvalDuration={promptEvalDuration}
        />
      )}
      {tools?.toolCalls && tools.toolCalls.length !== 0 && (
        <ToolCalls toolCalls={tools.toolCalls} />
      )}
      {ragResults.map((collection: ChromaDBGetResponse) => (
        <Accordion key={collection.name}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ fontSize: '0.8rem' }}>
              Top {collection.ids?.length} RAG matches from {collection.size} entries using an
              embedding vector of {collection.queryEmbedding?.length} dimensions
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <VectorVisualizer inline {...props.messageProps} {...props.metadata} rag={collection} />
          </AccordionDetails>
        </Accordion>
      ))}
      <JsonAccordion title="Full Response Details" value={message} />
    </>
  );
};

function PromptStats(props: {
  evalCount: number;
  evalDuration: number;
  promptEvalCount: number;
  promptEvalDuration: number;
}): JSX.Element {
  const rows = [
    ['Prompt', props.promptEvalCount, props.promptEvalDuration],
    ['Response', props.evalCount, props.evalDuration],
    [
      'Total',
      props.promptEvalCount + props.evalCount,
      props.promptEvalDuration + props.evalDuration,
    ],
  ];

  return (
    <TableContainer component={Card} className="PromptStats" sx={{ mb: 1 }}>
      <Table aria-label="prompt stats" size="small">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="right">Tokens</TableCell>
            <TableCell align="right">Time (s)</TableCell>
            <TableCell align="right">TPS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, count, duration]) => (
            <TableRow
              key={String(label)}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                {label}
              </TableCell>
              <TableCell align="right">{count}</TableCell>
              <TableCell align="right">{Math.round(Number(duration) / 10 ** 7) / 100}</TableCell>
              <TableCell align="right">
                {Math.round((Number(count) * 10 ** 9) / Number(duration))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ToolCalls(props: { toolCalls: Record<string, string>[] }): JSX.Element {
  return (
    <Accordion sx={{ boxSizing: 'border-box' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ fontSize: '0.8rem' }}>Tools queried</Box>
      </AccordionSummary>
      <AccordionDetails>
        {props.toolCalls.map((tool, index) => (
          <Box
            key={`${tool.name}-${index}`}
            sx={{
              m: 0,
              p: 1,
              pt: 0,
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #e0e0e0',
            }}
          >
            {index !== 0 && <Divider />}
            <Box
              sx={{
                fontSize: '0.75rem',
                display: 'flex',
                flexDirection: 'column',
                mt: 1,
                mb: 1,
                fontWeight: 'bold',
              }}
            >
              {tool.name}
            </Box>
            {tool.content !== 'null' ? (
              <JsonBlock value={JSON.parse(tool.content)} />
            ) : (
              'No response from tool call'
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

function JsonAccordion(props: { title: string; value: object }): JSX.Element {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ fontSize: '0.8rem' }}>{props.title}</Box>
      </AccordionSummary>
      <AccordionDetails>
        <JsonBlock value={props.value} />
      </AccordionDetails>
    </Accordion>
  );
}

function JsonBlock(props: { value: object }): JSX.Element {
  return (
    <JsonView
      displayDataTypes={false}
      objectSortKeys={true}
      collapsed={1}
      value={props.value}
      style={{ fontSize: '0.8rem', maxHeight: '20rem', overflow: 'auto' }}
    >
      <JsonView.String
        render={({ children, ...reset }): React.ReactNode => {
          if (typeof children === 'string' && children.match('\n')) {
            return (
              <pre {...reset} style={{ display: 'inline', border: 'none', ...reset.style }}>
                {children.trim()}
              </pre>
            );
          }
        }}
      />
    </JsonView>
  );
}
